import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "components/ui/Button";
import { ResultCorrectionNotice } from "features/results/components/ResultCorrectionNotice/ResultCorrectionNotice";
import { isCommonResultsTab } from "features/results/constants/messageTypeGroups";
import { useConfirmCommonResult } from "features/results/hooks/useConfirmCommonResult";
import { useConfirmRadiologyResult } from "features/results/hooks/useConfirmRadiologyResult";
import { useConfirmResult } from "features/results/hooks/useConfirmResult";
import {
  formatCommonDate,
  getPatientName,
} from "features/results/utils/formatters";
import { getResultReviewStatus } from "features/results/utils/resultReview";
import { pathGenerators } from "utils/routes";

import styles from "./ResultsDetailPanel.module.css";

const TYPE_LABELS = {
  laboratory: "Laboratory result",
  "laboratory-unmatched": "Laboratory result",
  radiology: "Radiology",
  "radiology-unmatched": "Radiology",
};

const getTypeLabel = (selectedTab, item) => {
  // Common-backed tabs (All Results + the message-type filtered tabs) mix
  // result kinds, so derive the label from the item's own resultType.
  if (isCommonResultsTab(selectedTab)) {
    const resultType = (item.resultType || "").toUpperCase();

    if (resultType.includes("LAB")) return "Laboratory result";
    if (resultType.includes("RAD")) return "Radiology";

    return item.resultType || "Other";
  }

  return TYPE_LABELS[selectedTab] || "-";
};

export const ResultsDetailPanel = ({
  item,
  selectedTab,
  staffMembers,
  onConfirm,
  assignSection,
  children,
  // On the patient's own Results page the name/DOB/"Open patient details" are
  // redundant — show only the type.
  hidePatientInfo = false,
  /*
   * Supplied where the panel replaces a list rather than sitting beside one, so the way back is
   * inside the card with the document it leaves, not floating above it.
   */
  onBack,
  /*
   * Opens the correction this result was replaced by, or the result it corrects. Supplied only
   * where that counterpart can be resolved from the rows already loaded (KIN-2089).
   */
  onOpenRelatedResult,
  /*
   * Step to the row above or below in the list beside the panel, so a GP can read
   * through the results without going back to the table. Undefined at either end of
   * the page. Where neither is supplied, as on the patient's own Results page, there
   * is no navigation.
   */
  onPrevious,
  onNext,
}) => {
  const { mutate: confirmLab, isPending: isPendingLab } = useConfirmResult();
  const { mutate: confirmRad, isPending: isPendingRad } =
    useConfirmRadiologyResult();
  const { mutate: confirmCommon, isPending: isPendingCommon } =
    useConfirmCommonResult();

  const navigate = useNavigate();
  const isPending = isPendingLab || isPendingRad || isPendingCommon;
  const isConfirmed = item.orderStatus === "CONFIRMED";

  const patientId = item.patientId ?? item.patient?.id;

  const handleOpenPatient = () => {
    if (!patientId) return;
    navigate(pathGenerators.patient.results(patientId));
  };

  const handleMarkAsReviewed = () => {
    if (isConfirmed || isPending) return;

    const ids = [item.id];
    const onSuccess = () => onConfirm?.();

    if (selectedTab === "radiology" || selectedTab === "radiology-unmatched") {
      confirmRad({ ids }, { onSuccess });
    } else if (isCommonResultsTab(selectedTab)) {
      confirmCommon({ ids }, { onSuccess });
    } else {
      confirmLab({ ids }, { onSuccess });
    }
  };

  const patientName = getPatientName(item);
  const dob = item.patient?.dateOfBirth
    ? formatCommonDate(item.patient.dateOfBirth)
    : null;

  const date = formatCommonDate(item.results?.[0]?.date || item.orderDate);
  const orderDate = formatCommonDate(item.orderDate);
  const typeLabel = getTypeLabel(selectedTab, item);

  const doctor = staffMembers?.find(
    (member) => member.id === item.patient?.doctorDetailsId
  );
  const orderBy = doctor ? `${doctor.firstName} ${doctor.lastName}` : "-";

  const referringDoctor = item.patient?.doctorDetails
    ? `${item.patient.doctorDetails.firstName} ${item.patient.doctorDetails.lastName}`
    : "-";
  const statusLabel = isConfirmed ? "Reviewed" : "Pending review";

  /*
   * Who reviewed it (KIN-2122). Shown as its own row rather than folded into Status,
   * which is scanned for one word. "Not recorded" is for the results reviewed before
   * KIN-1819 began recording a reviewer — there is no backfill, so most older results
   * read that way, and blank would read as nobody having reviewed them.
   */
  const review = getResultReviewStatus(item);
  const reviewedByLabel = review.reviewerName
    ? `${review.reviewerName}${review.reviewedAt ? ` on ${review.reviewedAt}` : ""}`
    : "Not recorded";

  return (
    <div className={styles.panel}>
      {(!!onPrevious || !!onNext) && (
        <div className={styles.navigation}>
          <Button
            variant="text"
            size="small"
            startIcon={ChevronLeft}
            onClick={onPrevious}
            disabled={!onPrevious}
          >
            Previous result
          </Button>

          <Button
            variant="text"
            size="small"
            endIcon={ChevronRight}
            onClick={onNext}
            disabled={!onNext}
          >
            Next result
          </Button>
        </div>
      )}

      <div className={styles.header}>
        <div className={styles.patientInfo}>
          {!!onBack && (
            <button className={styles.back} onClick={onBack} type="button">
              <ArrowLeft size={16} />
              Back to results
            </button>
          )}
          {!hidePatientInfo && (
            <span className={styles.patientName}>{patientName}</span>
          )}
          {!hidePatientInfo && !!dob && (
            <span className={styles.patientDob}>({dob})</span>
          )}
          {!hidePatientInfo && !!patientId && (
            <button className={styles.openPatient} onClick={handleOpenPatient}>
              Open patient details
            </button>
          )}
        </div>

        <button
          className={`${styles.markReviewed} ${
            isConfirmed ? styles.markReviewedDone : ""
          }`}
          onClick={handleMarkAsReviewed}
          disabled={isPending}
        >
          <CheckCircle size={16} />
          {isConfirmed ? "Reviewed" : "mark as reviewed"}
        </button>
      </div>

      {/* The document's own type, stated once. It used to appear three times over — as a pill
          beside the back link, as this heading, and again as the "Type" field below, with the
          pill and the field disagreeing because one carries the message type and the other the
          category it falls into. */}
      <ResultCorrectionNotice
        result={item}
        onOpenRelated={onOpenRelatedResult}
      />

      <h1 className={styles.title}>{item.resultType || typeLabel}</h1>

      <div className={styles.meta}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Date</span>
          <span className={styles.metaValue}>{date}</span>
        </div>

        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Order date</span>
          <span className={styles.metaValue}>{orderDate}</span>
        </div>

        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Order by</span>
          <span className={styles.metaValue}>{orderBy}</span>
        </div>

        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Status</span>
          <span className={styles.metaValue}>{statusLabel}</span>
        </div>

        {!!isConfirmed && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Reviewed by</span>
            <span className={styles.metaValue}>{reviewedByLabel}</span>
          </div>
        )}

        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Referring doctor</span>
          <span className={styles.metaValue}>{referringDoctor}</span>
        </div>
      </div>

      {!!assignSection && assignSection}

      <div className={styles.content}>{children}</div>
    </div>
  );
};
